import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { DEFAULT_INTERRUPTSOURCES, Idle } from "@ng-idle/core";
import { Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { OauthTokenResponse } from "../../models/oauth/oauth-token-response";
import { EMPTY, Observable, Subscription, timer } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { environment } from "../../../environments/environment";
import pkceChallenge from "pkce-challenge";
import { selectIsOauthTokenValid, selectOauthAccessTokenObject } from "../../stores/auth/auth.selector";
import { AuthOauthActions } from "../../stores/auth/auth.actions";
import { AppState } from "../../stores/app.state";
import { UserRouteTrackingService } from "../user-route-tracking/user-route-tracking.service";

@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {
  private pollingSubscription: Subscription | null = null;
  private activityDetected = false;

  private SESSION_POLLING_INTERVAL = 5000; //milliseconds
  // private readonly _oauthToken!: OauthToken | null;

  // get oauthToken(): OauthToken | null {
  //   return this._oauthToken;
  // }

  constructor(private userIdle: Idle,
              private router: Router,
              private http: HttpClient,
              private ngZone: NgZone,
              private store: Store<AppState>,
              private userRouteTrackingService: UserRouteTrackingService,
  ) {
    // console.log("initializing auth service")
    // this.store.select("oauthTokenReducer").subscribe(token => {
    //   // console.log("auth service getting token from store: ", token)
    //   this._oauthToken = token;
    // })
    // this._oauthToken = this.store.selectSignal(selectOauthTokenValue)();
  }

  randomBase64UrlString(byteLength: number = 32): string {
    const bytes = new Uint8Array(byteLength);
    crypto.getRandomValues(bytes);
    const bin = String.fromCharCode(...bytes);
    const b64 = btoa(bin);
    return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
  }


  async redirectToExternalLogin() {
    // sessionStorage.setItem("gotopage", window.location.pathname);
    this.userRouteTrackingService.setLastAtPage(window.location.pathname);
    // const stateChallengePair: { code_verifier: string, code_challenge: string } = await pkceChallenge(43);
    // const state = stateChallengePair.code_verifier.substring(0, 32);

    const state = this.randomBase64UrlString(32);
    this.store.dispatch(AuthOauthActions.saveOauthState({ oauthStateValue: state }));

    const codeChallengePair: { code_verifier: string, code_challenge: string } = await pkceChallenge(64);
    const codeVerifier = codeChallengePair.code_verifier;
    const codeChallenge = codeChallengePair.code_challenge;
    // console.log({ state, codeVerifier, codeChallenge })
    // this.store.dispatch(AuthOauthActions.saveOauthState({ oauthStateValue: state }));
    this.store.dispatch(AuthOauthActions.saveOauthCodeVerifier({ oauthCodeVerifierValue: codeVerifier }));

    const params = new URLSearchParams();
    params.set('response_type', 'code');
    params.set('client_id', environment.oauth2ClientId);
    params.set('redirect_uri', `${environment.oauth2RedirectUri}`);
    params.set('state', state);
    params.set('code_challenge', codeChallenge);
    params.set('code_challenge_method', 'S256');

    const authUrl = `${environment.oauth2ApiEndpoint}/authorize?${params.toString()}`;
    window.location.href = authUrl;
  }

  // canActivateEmailPage(): boolean {
  //   if (environment.name == "DEV") return true;
  //   // console.log(this._oauthToken)
  //   // return !!this._oauthToken &&
  //   //   !!this._oauthToken.accessToken &&
  //   //   !!this._oauthToken.refreshToken &&
  //   //   !this._oauthToken.accessToken.isTokenExpired();
  //   return this.store.selectSignal(selectIsOauthTokenValid)();
  // }

  invalidateSession() {
    localStorage.clear();
    sessionStorage.clear();
  }

  private clearAllCookies(): void {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
      document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
    }
  }


  signOut() {
    this.invalidateSession();
    this.clearAllCookies();
    window.location.href = environment.oauth2SignOutUrl;
  }

  private timeOut(): void {
    window.location.replace(`${environment.oauth2TimeOutUrl}`);
  }

  private endApplicationSession(): void {
    this.stopSessionPolling();
    this.stopSessionExpiryWatcher();
    this.invalidateSession();
    this.clearAllCookies();
  }

  exchangeCodeForToken(code: string, codeVerifier: string, clientId: string): Observable<OauthTokenResponse> {
    const body = {
      'code': code,
      'codeVerifier': codeVerifier,
      'clientId': clientId,
    }
    return this.http.post<OauthTokenResponse>(`${environment.serverUrl}/oauth2/exchangetoken`, body);
  }

  startSessionExpiryWatcher(): void {
    this.userIdle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    this.userIdle.onInterrupt.subscribe(() => {
      this.activityDetected = true;
    });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.activityDetected = true;
    });
    window.addEventListener('load', () => {
      this.activityDetected = true;
    });

    this.userIdle.watch();
  }

  stopSessionExpiryWatcher(): void {
    // console.log("stop watching");
    this.userIdle.stop();
  }

  startSessionPolling(): void {
    if (environment.name == "DEV") return;
    const oauthAccessToken = this.store.selectSignal(selectOauthAccessTokenObject)();
    const cstoken = oauthAccessToken?.cstoken;
    if (!cstoken) {
      console.error('Unable to poll session: missing cstoken.');
      //TODO handle empty cstoken?
      return;
    }

    const headers = new HttpHeaders({ 'x-cstoken': cstoken });

    this.ngZone.runOutsideAngular(() => {
      // console.log('Starting session polling...');
      this.pollingSubscription = timer(0, this.SESSION_POLLING_INTERVAL).pipe(
        switchMap(() => {
          return this.http.get(`${environment.oauth2SessionCheckEndpoint}`, {
            headers,
            withCredentials: true,
            responseType: 'text'
          }).pipe(
            switchMap(() => {
              if (this.activityDetected) {
                this.activityDetected = false;
                return this.http.get(`${environment.oauth2SessionUpdateEndpoint}`, {
                  headers,
                  withCredentials: true
                });
              }
              return EMPTY;
            }),
            catchError(err => {
              this.ngZone.run(() => {
                switch (err.status) {
                  case 404:
                    console.warn('404: No session found. Ending session and redirecting to timeout page.');
                    this.endApplicationSession();
                    this.timeOut();
                    break;
                  case 401:
                    console.warn('401: Session mismatch. Re-authenticating.');
                    this.endApplicationSession();
                    this.redirectToExternalLogin();
                    break;
                  default:
                    console.error(err.message);
                }
              });
              return EMPTY;
            })
          );
        })
      ).subscribe();
    });
  }

  stopSessionPolling(): void {
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = null;
    // console.log('Stopping session polling...');
  }

  ngOnDestroy() {
    this.stopSessionPolling();
    this.stopSessionExpiryWatcher();
  }

}
